import { useState, useEffect, useCallback } from "react";
import {
  MessageCircle,
  Send,
  CheckCircle,
  CheckCheck,
  XCircle,
  MinusCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  RefreshCw,
  Filter,
  Clock,
  Info,
  Phone,
} from "lucide-react";
import api from "@/lib/api-client";

interface WhatsAppLogCustomer {
  firstName: string;
  lastName: string;
  phone: string;
}

interface WhatsAppLogLocation {
  name: string;
}

interface WhatsAppLogReservation {
  confirmationCode: string;
}

type WhatsAppSendStatus = "SENT" | "FAILED" | "SKIPPED";
type WhatsAppDeliveryStatus = "sent" | "delivered" | "read" | "failed";

interface WhatsAppLog {
  id: string;
  createdAt: string;
  templateName: string;
  status: WhatsAppSendStatus;
  deliveryStatus: WhatsAppDeliveryStatus | null;
  deliveredAt: string | null;
  readAt: string | null;
  errorMessage: string | null;
  deliveryError: string | null;
  metaMessageId: string | null;
  reservationId: string | null;
  customerId: string | null;
  locationId: string | null;
  customer: WhatsAppLogCustomer | null;
  location: WhatsAppLogLocation | null;
  reservation: WhatsAppLogReservation | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface WhatsAppLogsResponse {
  success: boolean;
  logs: WhatsAppLog[];
  pagination: Pagination;
}

interface WhatsAppStats {
  total: number;
  byStatus: Record<string, number>;
  byDeliveryStatus: Record<string, number>;
}

interface WhatsAppStatsResponse {
  success: boolean;
  stats: WhatsAppStats;
}

interface OrganizationOption {
  id: string;
  name: string;
}

interface OrganizationsResponse {
  success: boolean;
  data: OrganizationOption[];
}

const STATUS_CONFIG: Record<
  WhatsAppSendStatus,
  { label: string; color: string; bgColor: string; icon: typeof Send }
> = {
  SENT: {
    label: "Enviado",
    color: "text-green-700",
    bgColor: "bg-green-100",
    icon: CheckCircle,
  },
  FAILED: {
    label: "Fallido",
    color: "text-red-700",
    bgColor: "bg-red-100",
    icon: XCircle,
  },
  SKIPPED: {
    label: "Omitido",
    color: "text-gray-600",
    bgColor: "bg-gray-100",
    icon: MinusCircle,
  },
};

const DELIVERY_STATUS_CONFIG: Record<
  WhatsAppDeliveryStatus,
  { label: string; color: string; bgColor: string; icon: typeof Send }
> = {
  sent: {
    label: "Enviado a Meta",
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    icon: Send,
  },
  delivered: {
    label: "Entregado",
    color: "text-green-700",
    bgColor: "bg-green-100",
    icon: CheckCheck,
  },
  read: {
    label: "Leído",
    color: "text-sky-700",
    bgColor: "bg-sky-100",
    icon: CheckCheck,
  },
  failed: {
    label: "Fallo entrega",
    color: "text-red-700",
    bgColor: "bg-red-100",
    icon: XCircle,
  },
};

export default function AdminWhatsApp() {
  const [logs, setLogs] = useState<WhatsAppLog[]>([]);
  const [stats, setStats] = useState<WhatsAppStats | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<WhatsAppLog | null>(null);

  // Filtro por organización (cliente)
  const [organizations, setOrganizations] = useState<OrganizationOption[]>(
    [],
  );
  const [organizationFilter, setOrganizationFilter] = useState(""); // id, "" = todas
  const [orgQuery, setOrgQuery] = useState(""); // texto mostrado en el buscador

  const orgIdByName = useCallback(
    (name: string) => organizations.find((o) => o.name === name)?.id,
    [organizations],
  );

  const buildParams = useCallback(
    (page: number) => {
      const params: Record<string, string> = {
        page: String(page),
        limit: "50",
      };
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      if (deliveryStatusFilter !== "all")
        params.deliveryStatus = deliveryStatusFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (organizationFilter) params.organizationId = organizationFilter;
      return params;
    },
    [
      search,
      statusFilter,
      deliveryStatusFilter,
      dateFrom,
      dateTo,
      organizationFilter,
    ],
  );

  const fetchLogs = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const res = await api.get<WhatsAppLogsResponse>(
          "/admin/whatsapp/logs",
          { params: buildParams(page) },
        );
        setLogs(res.data.logs);
        setPagination(res.data.pagination);
      } catch (error) {
        console.error("Error fetching whatsapp logs:", error);
        setLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [buildParams],
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get<WhatsAppStatsResponse>(
        "/admin/whatsapp/logs/stats",
        { params: buildParams(1) },
      );
      setStats(res.data.stats);
    } catch (error) {
      console.error("Error fetching whatsapp stats:", error);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  // Lista de organizaciones para el selector de filtro (se carga una sola vez)
  useEffect(() => {
    api
      .get<OrganizationsResponse>("/admin/organizations", {
        params: { limit: "1000" },
      })
      .then((res) => {
        const sorted = [...res.data.data].sort((a, b) =>
          a.name.localeCompare(b.name, "es"),
        );
        setOrganizations(sorted);
      })
      .catch((error) => {
        console.error("Error fetching organizations:", error);
      });
  }, []);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  };

  const getStatusBadge = (status: WhatsAppSendStatus) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.SKIPPED;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getDeliveryBadge = (deliveryStatus: WhatsAppDeliveryStatus | null) => {
    if (!deliveryStatus) {
      return <span className="text-xs text-gray-300">—</span>;
    }
    const config = DELIVERY_STATUS_CONFIG[deliveryStatus];
    if (!config) return <span className="text-xs text-gray-300">—</span>;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getCustomerName = (log: WhatsAppLog) =>
    log.customer
      ? `${log.customer.firstName} ${log.customer.lastName}`.trim()
      : "—";

  const getReason = (log: WhatsAppLog) => log.deliveryError || log.errorMessage;

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            WhatsApp
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Historial de mensajes de WhatsApp enviados desde la plataforma
          </p>
        </div>
        <button
          onClick={() => {
            fetchLogs(pagination.page);
            fetchStats();
          }}
          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-600 self-start sm:self-auto"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Nota informativa */}
      <div className="mb-4 md:mb-6 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>
          Registro de mensajes salientes de WhatsApp. El estado de entrega
          (entregado/leído) aparece cuando Meta lo reporta.
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Total</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Send className="w-4 h-4" />
              <span className="text-xs font-medium">Enviados</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {stats.byStatus.SENT ?? 0}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <CheckCheck className="w-4 h-4" />
              <span className="text-xs font-medium">Entregados</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {stats.byDeliveryStatus.delivered ?? 0}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <CheckCheck className="w-4 h-4" />
              <span className="text-xs font-medium">Leídos</span>
            </div>
            <p className="text-2xl font-bold text-sky-600">
              {stats.byDeliveryStatus.read ?? 0}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <XCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Fallidos</span>
            </div>
            <p className="text-2xl font-bold text-red-600">
              {stats.byStatus.FAILED ?? 0}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {stats.byStatus.SKIPPED ?? 0} omitidos
            </p>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="bg-white rounded-xl border border-gray-200 mb-4">
        <div className="p-3 md:p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o teléfono del cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchLogs(1)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
              showFilters
                ? "border-primary-500 bg-primary-50 text-primary-700"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            <Filter className="w-4 h-4" />
            Filtros
          </button>
          <button
            onClick={() => fetchLogs(1)}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800"
          >
            Buscar
          </button>
        </div>
        {showFilters && (
          <div className="px-3 md:px-4 pb-3 md:pb-4 flex flex-col sm:flex-row flex-wrap gap-3 border-t border-gray-100 pt-3">
            <div className="relative w-full sm:w-56">
              <input
                type="text"
                list="admin-whatsapp-organizations"
                placeholder="Todas las organizaciones"
                value={orgQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setOrgQuery(value);
                  if (value === "") {
                    setOrganizationFilter("");
                    return;
                  }
                  const matchedId = orgIdByName(value);
                  if (matchedId) setOrganizationFilter(matchedId);
                }}
                onBlur={() => {
                  if (organizationFilter) {
                    const selected = organizations.find(
                      (o) => o.id === organizationFilter,
                    );
                    setOrgQuery(selected ? selected.name : "");
                  } else if (!orgIdByName(orgQuery)) {
                    setOrgQuery("");
                  }
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <datalist id="admin-whatsapp-organizations">
                {organizations.map((org) => (
                  <option key={org.id} value={org.name} />
                ))}
              </datalist>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Todos los estados de envío</option>
              {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                <option key={value} value={value}>
                  {config.label}
                </option>
              ))}
            </select>
            <select
              value={deliveryStatusFilter}
              onChange={(e) => setDeliveryStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Todos los estados de entrega</option>
              {Object.entries(DELIVERY_STATUS_CONFIG).map(
                ([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ),
              )}
            </select>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">
                Desde
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">
                Hasta
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <MessageCircle className="w-12 h-12 mb-3" />
            <p className="text-sm">No se encontraron mensajes</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedLog(log)}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {getCustomerName(log)}
                      </p>
                      {log.customer && (
                        <p className="text-xs text-gray-500 truncate">
                          {log.customer.phone}
                        </p>
                      )}
                    </div>
                    {getStatusBadge(log.status)}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100">
                      {log.templateName}
                    </span>
                    <span>{formatDate(log.createdAt)}</span>
                    {log.reservation?.confirmationCode && (
                      <span className="text-gray-400">
                        #{log.reservation.confirmationCode}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    {getDeliveryBadge(log.deliveryStatus)}
                    {getReason(log) && (
                      <span className="text-xs text-red-500 truncate">
                        {getReason(log)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Fecha
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Cliente
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Plantilla
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Origen / Reserva
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Estado de envío
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Entrega
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Motivo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(log.createdAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[200px]">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {getCustomerName(log)}
                          </p>
                          {log.customer && (
                            <p className="text-xs text-gray-500 truncate">
                              {log.customer.phone}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 whitespace-nowrap">
                          {log.templateName}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">
                          {log.reservation?.confirmationCode
                            ? `#${log.reservation.confirmationCode}`
                            : "—"}
                        </p>
                        {log.location?.name && (
                          <p className="text-[11px] text-gray-400 truncate max-w-[140px]">
                            {log.location.name}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                      <td className="px-4 py-3">
                        {getDeliveryBadge(log.deliveryStatus)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-red-500 truncate max-w-[220px]">
                          {getReason(log) || (
                            <span className="text-gray-300">—</span>
                          )}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              {pagination.total} mensajes total · Página {pagination.page} de{" "}
              {pagination.totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90dvh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Detalle del mensaje
              </h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    Cliente
                  </label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {getCustomerName(selectedLog)}
                  </p>
                  {selectedLog.customer && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {selectedLog.customer.phone}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    Estado de envío
                  </label>
                  <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    Plantilla
                  </label>
                  <p className="text-sm text-gray-700 mt-0.5">
                    {selectedLog.templateName}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    Ubicación
                  </label>
                  <p className="text-sm text-gray-700 mt-0.5">
                    {selectedLog.location?.name || "—"}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Reserva
                </label>
                <p className="text-sm text-gray-700 mt-0.5">
                  {selectedLog.reservation?.confirmationCode
                    ? `#${selectedLog.reservation.confirmationCode}`
                    : "—"}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-500 mb-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-xs">Enviado</span>
                  </div>
                  <p className="text-xs font-medium text-gray-700">
                    {formatDate(selectedLog.createdAt)}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                    <CheckCheck className="w-3.5 h-3.5" />
                    <span className="text-xs">Entregado</span>
                  </div>
                  <p className="text-xs font-medium text-green-700">
                    {selectedLog.deliveredAt
                      ? formatDate(selectedLog.deliveredAt)
                      : "—"}
                  </p>
                </div>
                <div className="bg-sky-50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-sky-600 mb-1">
                    <CheckCheck className="w-3.5 h-3.5" />
                    <span className="text-xs">Leído</span>
                  </div>
                  <p className="text-xs font-medium text-sky-700">
                    {selectedLog.readAt ? formatDate(selectedLog.readAt) : "—"}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Estado de entrega
                </label>
                <div className="mt-1">
                  {getDeliveryBadge(selectedLog.deliveryStatus)}
                </div>
              </div>

              {getReason(selectedLog) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-700 mb-0.5">
                    Motivo
                  </p>
                  <p className="text-sm text-red-600">
                    {getReason(selectedLog)}
                  </p>
                </div>
              )}

              {selectedLog.metaMessageId && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    Meta Message ID
                  </label>
                  <p className="text-xs text-gray-500 font-mono mt-0.5 break-all">
                    {selectedLog.metaMessageId}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
